  if (!customElements.get('video-stories')) {
    customElements.define('video-stories', class VideoStories extends HTMLElement {
      constructor() {
        super();
        this._onKeydown = this._onKeydown.bind(this);
        this._onResize = this._onResize.bind(this);
      }

      static get TIMINGS() {
        return {
          tapMaxMs: 300,
          touchMoveTolerance: 10,
          swipeHorizontalThreshold: 50,
          swipeDownThreshold: 100,
          slideAnimationMs: 280,
          slideTranslatePx: 40,
          railEndThreshold: 8
        };
      }

      connectedCallback() {
        // Respect prefers-reduced-motion: pause first bubble autoplay
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.querySelectorAll('.vs__bubble[data-autoplay="true"] video').forEach(v => {
            v.removeAttribute('autoplay');
            v.removeAttribute('loop');
            try { v.pause(); } catch (_) {}
          });
        }

        this.rail = this.querySelector('.vs__rail');
        this.modal = this.querySelector('.vs__modal');
        this.bubbles = Array.from(this.querySelectorAll('.vs__bubble'));
        this.activeIndex = 0;

        this.cartAddUrl = this.getAttribute('cart-add-url');
        this.cartUrl = this.getAttribute('cart-url');
        this.addToCartLabel = this.getAttribute('add-to-cart-label') || 'Ajouter au panier';
        this.selectOptionsLabel = this.getAttribute('select-options-label') || 'Choisir une option';

        this.ariaLabels = {
          close: this.getAttribute('aria-close-label') || 'Fermer',
          play: this.getAttribute('aria-play-label') || 'Lecture / Pause',
          mute: this.getAttribute('aria-mute-label') || 'Activer / couper le son',
          prev: this.getAttribute('aria-prev-label') || 'Vidéo précédente',
          next: this.getAttribute('aria-next-label') || 'Vidéo suivante'
        };

        this._setAriaLabels();
        this._wireRailScroll();
        this._wireBubbleClicks();
        this._wireBubbleHover();
        this._wireControls();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeydown);
        window.removeEventListener('resize', this._onResize);
        if (this._upsellTouchAbort) this._upsellTouchAbort.abort();
      }

      _onKeydown(e) {
        // Don't hijack Space/Enter when focus is on a form field or button
        if (e.target.matches && e.target.matches('input, textarea, select, button, [contenteditable=""], [contenteditable="true"]')) return;
        if (!this.modal.hasAttribute('open')) return;
        switch (e.key) {
          case 'ArrowLeft': this._navigate(-1); e.preventDefault(); break;
          case 'ArrowRight': this._navigate(1); e.preventDefault(); break;
          case ' ': this._togglePlay(); e.preventDefault(); break;
          case 'm': case 'M': this._toggleMute(); e.preventDefault(); break;
        }
      }

      _onResize() {
        if (!this.rail) return;
        const isEnd = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - VideoStories.TIMINGS.railEndThreshold;
        this.classList.toggle('is-scrolled-end', isEnd);
      }

      _setAriaLabels() {
        const map = {
          '[data-action="close"]': this.ariaLabels.close,
          '[data-action="play"]': this.ariaLabels.play,
          '[data-action="mute"]': this.ariaLabels.mute,
          '[data-action="prev"]': this.ariaLabels.prev,
          '[data-action="next"]': this.ariaLabels.next
        };
        Object.entries(map).forEach(([sel, label]) => {
          const el = this.querySelector(sel);
          if (el) el.setAttribute('aria-label', label);
        });
      }

      _wireRailScroll() {
        if (!this.rail) return;
        this.rail.addEventListener('scroll', this._onResize, { passive: true });
        // initial check (deferred so layout is ready)
        requestAnimationFrame(this._onResize);
        window.addEventListener('resize', this._onResize, { passive: true });
      }

      _wireBubbleClicks() {
        this.bubbles.forEach((b, i) => {
          b.addEventListener('click', () => this.openAt(i));
        });
      }

      _wireBubbleHover() {
        // Skip on touch-only devices (no real hover capability)
        if (!window.matchMedia('(hover: hover)').matches) return;
        this.bubbles.forEach((b, i) => {
          b.addEventListener('mouseenter', () => this._previewBubble(i));
          b.addEventListener('mouseleave', () => this._resetBubblesToDefault());
        });
      }

      _previewBubble(idx) {
        this.bubbles.forEach((b, i) => {
          const video = b.querySelector('video');
          if (!video) return;
          if (i === idx) {
            video.currentTime = 0;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      _resetBubblesToDefault() {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.bubbles.forEach((b) => {
          const video = b.querySelector('video');
          if (!video) return;
          const isAutoplay = b.dataset.autoplay === 'true';
          if (isAutoplay && !reducedMotion) {
            video.currentTime = 0;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      openAt(index) {
        if (!this.modal || index < 0 || index >= this.bubbles.length) return;

        this.activeIndex = index;
        this._renderActive();

        // pause any autoplaying bubble videos
        this.querySelectorAll('.vs__bubble video').forEach(v => v.pause());

        // lock body scroll + compensate for the scrollbar width to prevent layout reflow
        this._prevBodyOverflow = document.body.style.overflow;
        this._prevBodyPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = scrollbarWidth + 'px';
        }

        if (typeof this.modal.showModal === 'function') {
          this.modal.showModal();
        } else {
          this.modal.setAttribute('open', '');
        }

        // Reset scroll position on the dialog itself in case a swipe leaked through
        this.modal.scrollTop = 0;
        this.modal.scrollLeft = 0;

        // Move focus to the (non-interactive) frame so the dialog's auto-focus doesn't
        // land on the upsell CTA and paint a focus-visible ring on it.
        const frame = this.modal.querySelector('.vs__modal-frame');
        if (frame) {
          if (!frame.hasAttribute('tabindex')) frame.setAttribute('tabindex', '-1');
          frame.focus({ preventScroll: true });
        }

        // Opening animation: scale up + fade in (skipped if reduced-motion)
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (frame && !reduceMotion) {
          frame.animate([
            { transform: 'scale(0.55)', opacity: 0 },
            { transform: 'scale(1)', opacity: 1 }
          ], { duration: 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
        }

        // play active video with sound
        const video = this.modal.querySelector('.vs__modal-video');
        if (video) {
          video.muted = this._userMuted ?? false;
          video.currentTime = 0;
          video.play().catch(() => {
            // navigateur bloque autoplay → start muted as fallback
            video.muted = true;
            video.play().catch(() => {});
          });
        }

        this._preloadNext();

        this._pushDataLayer('video_modal_open');
        this._pushDataLayer('video_start');
      }

      _renderActive() {
        const bubble = this.bubbles[this.activeIndex];
        if (!bubble) return;

        const video = this.modal.querySelector('.vs__modal-video');
        const titleEl = this.modal.querySelector('.vs__modal-title');
        const avatar = this.modal.querySelector('.vs__modal-avatar');
        const bg = this.modal.querySelector('.vs__modal-bg');

        const src = bubble.dataset.videoSrc;
        const poster = bubble.dataset.poster;
        const title = bubble.dataset.title || '';
        const avatarSrc = bubble.dataset.avatar || '';

        if (titleEl) titleEl.textContent = title;
        if (avatar) {
          if (avatarSrc) { avatar.src = avatarSrc; avatar.style.display = ''; }
          else { avatar.removeAttribute('src'); avatar.style.display = 'none'; }
        }
        if (bg) bg.style.backgroundImage = poster ? `url(${poster})` : '';
        if (video) {
          if (video.src !== src && src) video.src = src;
          if (poster) video.poster = poster;
        }

        this._renderUpsell();
      }

      _renderUpsell() {
        const bubble = this.bubbles[this.activeIndex];
        const card = this.modal.querySelector('.vs__upsell-card');
        const track = this.modal.querySelector('.vs__upsell-track');
        const dots = this.modal.querySelector('.vs__upsell-dots');
        if (!card || !track) return;

        let products = [];
        try { products = JSON.parse(bubble.dataset.products || '[]'); } catch (_) {}

        // Reset any prior touch listeners attached to the persistent card
        if (this._upsellTouchAbort) this._upsellTouchAbort.abort();
        this._upsellTouchAbort = new AbortController();
        const upsellSignal = this._upsellTouchAbort.signal;

        if (!products.length) {
          card.hidden = true;
          if (dots) {
            dots.hidden = true;
            dots.innerHTML = '';
          }
          return;
        }
        card.hidden = false;
        // Default to the first available product (skip unavailable ones).
        // If none are available, fall back to index 0.
        const firstAvailableIdx = products.findIndex(p => p.available);
        this._activeProductIndex = firstAvailableIdx >= 0 ? firstAvailableIdx : 0;

        track.innerHTML = products.map((p, i) => `
          <div class="vs__upsell-slide" data-product-index="${i}" data-product-id="${p.id}">
            ${p.image ? `<div class="vs__upsell-image"><img src="${this._esc(p.image)}" alt="" loading="lazy" decoding="async" /></div>` : '<div class="vs__upsell-image"></div>'}
            <div class="vs__upsell-info">
              <p class="vs__upsell-name">${this._esc(p.title)}</p>
              <p class="vs__upsell-price">${this._esc(p.price_formatted || '')}</p>
              <button type="button" class="vs__upsell-cta" data-product-id="${p.id}" ${p.available ? '' : 'disabled'}>
                ${p.available ? this._esc(this.addToCartLabel) : 'Indisponible'}
              </button>
            </div>
          </div>
        `).join('');

        track.style.transform = `translateX(-${this._activeProductIndex * 100}%)`;

        if (products.length > 1) {
          dots.hidden = false;
          dots.innerHTML = products.map((_, i) => `
            <button type="button" class="vs__upsell-dot" data-dot-index="${i}" aria-current="${i === this._activeProductIndex ? 'true' : 'false'}"></button>
          `).join('');
          dots.querySelectorAll('.vs__upsell-dot').forEach(d => {
            d.addEventListener('click', () => this._setUpsellIndex(parseInt(d.dataset.dotIndex, 10)));
          });
        } else {
          dots.hidden = true;
          dots.innerHTML = '';
        }

        // Wire CTA clicks
        track.querySelectorAll('.vs__upsell-cta').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._handleAddClick(btn);
          });
        });

        // Touch swipe sur la card (mobile)
        if (products.length > 1) {
          let cardStartX = 0, cardStartT = 0, cardMoved = false;
          card.addEventListener('touchstart', (e) => {
            const t = e.touches[0];
            cardStartX = t.clientX;
            cardStartT = Date.now();
            cardMoved = false;
          }, { passive: true, signal: upsellSignal });
          card.addEventListener('touchmove', (e) => {
            const t = e.touches[0];
            if (Math.abs(t.clientX - cardStartX) > 10) cardMoved = true;
          }, { passive: true, signal: upsellSignal });
          card.addEventListener('touchend', (e) => {
            if (!cardMoved) return;
            const t = e.changedTouches[0];
            const dx = t.clientX - cardStartX;
            const dt = Date.now() - cardStartT;
            if (Math.abs(dx) > 40 && dt < 600) {
              const dir = dx < 0 ? 1 : -1;
              const nextIdx = Math.max(0, Math.min(products.length - 1, this._activeProductIndex + dir));
              this._setUpsellIndex(nextIdx);
              e.stopPropagation(); // empêche le swipe vidéo de prendre le relais
            }
          }, { signal: upsellSignal });
        }
      }

      _setUpsellIndex(i) {
        const track = this.modal.querySelector('.vs__upsell-track');
        const dots = this.modal.querySelector('.vs__upsell-dots');
        if (!track) return;
        const products = track.querySelectorAll('.vs__upsell-slide').length;
        if (i < 0 || i >= products) return;
        this._activeProductIndex = i;
        track.style.transform = `translateX(-${i * 100}%)`;
        if (dots) {
          dots.querySelectorAll('.vs__upsell-dot').forEach((d, idx) => {
            d.setAttribute('aria-current', idx === i ? 'true' : 'false');
          });
        }
      }

      _handleAddClick(btn) {
        const productId = parseInt(btn.dataset.productId, 10);
        const slide = btn.closest('.vs__upsell-slide');
        const bubble = this.bubbles[this.activeIndex];
        let products = [];
        try { products = JSON.parse(bubble.dataset.products || '[]'); } catch (_) {}
        const product = products.find(p => p.id === productId);
        if (!product) return;

        const availableVariants = (product.variants || []).filter(v => v.available);

        // Cas single variant → ajout direct
        if (availableVariants.length <= 1) {
          const variantId = availableVariants[0] ? availableVariants[0].id : product.variants[0].id;
          this._addToCart(variantId, btn);
          return;
        }

        // Cas multi-variant : si pills déjà ouvertes ET un variant sélectionné → ajout
        const selectedPill = slide.querySelector('.vs__variant-pill[aria-pressed="true"]');
        if (selectedPill) {
          const variantId = parseInt(selectedPill.dataset.variantId, 10);
          this._addToCart(variantId, btn);
          return;
        }

        // Sinon, transformer le bouton en pills
        this._showVariantPills(slide, btn, availableVariants);
      }

      _showVariantPills(slide, btn, variants) {
        // Prevent double init
        if (slide.querySelector('.vs__variant-pills')) return;

        const pillsHtml = `
          <div class="vs__variant-prompt">${this._esc(this.selectOptionsLabel)}</div>
          <div class="vs__variant-pills">
            ${variants.map(v => `
              <button type="button" class="vs__variant-pill" data-variant-id="${v.id}" aria-pressed="false">
                ${this._esc(v.title)}
              </button>
            `).join('')}
          </div>
        `;
        const cta = btn;
        cta.style.display = 'none';

        const wrap = document.createElement('div');
        wrap.className = 'vs__variant-wrap';
        wrap.innerHTML = pillsHtml;
        // Insert pills above the CTA in the info column so they stack vertically
        const info = cta.parentElement;
        info.insertBefore(wrap, cta);

        wrap.querySelectorAll('.vs__variant-pill').forEach(pill => {
          pill.addEventListener('click', () => {
            wrap.querySelectorAll('.vs__variant-pill').forEach(p => p.setAttribute('aria-pressed', 'false'));
            pill.setAttribute('aria-pressed', 'true');
            cta.style.display = '';
          });
        });
      }

      async _addToCart(variantId, btnEl) {
        if (!variantId) return;
        const originalText = btnEl.textContent;
        btnEl.disabled = true;
        btnEl.textContent = '…';

        try {
          const r = await fetch(this.cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });

          if (!r.ok) throw new Error('cart add failed');

          // Notifier le thème (le <cart-drawer> du thème écoute cet évènement avec detail.open=true pour s'ouvrir)
          document.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: true }
          }));

          this._pushDataLayer('video_add_to_cart', {
            variant_id: variantId,
            quantity: 1
          });

          // Fallback: si pas de cart-drawer écoutant, naviguer vers /cart après un court délai
          this._maybeFallbackToCart();

          // Fermer le modal vidéo après ajout réussi pour laisser place au cart drawer
          this.close();
        } catch (e) {
          console.warn('[video-stories] add to cart failed', e);
          // fallback : naviguer vers /cart
          window.location.href = this.cartUrl || '/cart';
        } finally {
          btnEl.disabled = false;
          btnEl.textContent = originalText;
        }
      }

      _maybeFallbackToCart() {
        // The theme's <cart-drawer> handles cart:refresh automatically. If it's
        // absent (e.g. no drawer in this template), fall back after a short delay.
        const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
        if (drawer) return;
        setTimeout(() => { window.location.href = this.cartUrl || '/cart'; }, 300);
      }

      _esc(str) {
        return String(str).replace(/[&<>"']/g, s => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[s]));
      }

      _pushDataLayer(eventName, extra) {
        if (typeof gtag === 'undefined') return;
        // window.dataLayer = window.dataLayer || [];
        const bubble = this.bubbles[this.activeIndex];
        const payload = Object.assign({
          //event: eventName,
          video_section_id: this.id || null,
          video_index: this.activeIndex,
          video_title: bubble ? (bubble.dataset.title || '') : ''
        }, extra || {});
        //window.dataLayer.push(payload);
        gtag('event', eventName, payload);
      }

      close() {
        if (!this.modal) return;
        this._pushDataLayer('video_modal_close');
        const video = this.modal.querySelector('.vs__modal-video');
        if (video) { video.pause(); video.currentTime = 0; }
        if (typeof this.modal.close === 'function') this.modal.close();
        else this.modal.removeAttribute('open');
        document.body.style.overflow = this._prevBodyOverflow || '';
        document.body.style.paddingRight = this._prevBodyPaddingRight || '';
        // reprise de l'autoplay sur la bulle 1, sauf si l'utilisateur a opté pour reduced-motion
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          const firstVideo = this.querySelector('.vs__bubble[data-autoplay="true"] video');
          if (firstVideo) firstVideo.play().catch(() => {});
        }
      }

      _wireControls() {
        if (!this.modal) return;

        const onAction = (action) => {
          switch (action) {
            case 'close': this.close(); break;
            case 'play': this._togglePlay(); break;
            case 'mute': this._toggleMute(); break;
            case 'prev': this._navigate(-1); break;
            case 'next': this._navigate(1); break;
          }
        };

        this.modal.querySelectorAll('[data-action]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            onAction(btn.dataset.action);
          });
        });

        // Close on any click outside the video frame (backdrop, dialog padding, empty stage space).
        // Nav buttons stopPropagation so they don't trigger this; controls are inside the frame.
        this.modal.addEventListener('click', (e) => {
          const frame = this.modal.querySelector('.vs__modal-frame');
          if (frame && !frame.contains(e.target)) this.close();
        });

        // Close event (ESC native)
        this.modal.addEventListener('close', () => {
          document.body.style.overflow = this._prevBodyOverflow || '';
          document.body.style.paddingRight = this._prevBodyPaddingRight || '';
          // reprise de l'autoplay sur la bulle 1, sauf si l'utilisateur a opté pour reduced-motion
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            const firstVideo = this.querySelector('.vs__bubble[data-autoplay="true"] video');
            if (firstVideo) firstVideo.play().catch(() => {});
          }
        });

        // Keyboard shortcuts when modal open
        document.addEventListener('keydown', this._onKeydown);

        // Sync icons with video state + auto-advance to next video on end (style Stories)
        const video = this.modal.querySelector('.vs__modal-video');
        if (video) {
          video.addEventListener('play', () => this._syncPlayIcon());
          video.addEventListener('pause', () => this._syncPlayIcon());
          video.addEventListener('volumechange', () => this._syncMuteIcon());
          video.addEventListener('ended', () => {
            this._pushDataLayer('video_complete');
            if (this.modal.hasAttribute('open') && this.bubbles.length > 1) {
              this._navigate(1);
            }
          });
        }

        // Touch gestures (mobile)
        const tapArea = this.modal.querySelector('[data-tap-area]');
        if (tapArea) {
          let touchStartX = 0, touchStartY = 0, touchStartT = 0, touchHasMoved = false;

          tapArea.addEventListener('touchstart', (e) => {
            if (e.touches.length > 1) return;
            const t = e.touches[0];
            touchStartX = t.clientX;
            touchStartY = t.clientY;
            touchStartT = Date.now();
            touchHasMoved = false;
          }, { passive: true });

          tapArea.addEventListener('touchmove', (e) => {
            const t = e.touches[0];
            if (Math.abs(t.clientX - touchStartX) > VideoStories.TIMINGS.touchMoveTolerance || Math.abs(t.clientY - touchStartY) > VideoStories.TIMINGS.touchMoveTolerance) {
              touchHasMoved = true;
            }
          }, { passive: true });

          tapArea.addEventListener('touchend', (e) => {
            const t = e.changedTouches[0];
            const dx = t.clientX - touchStartX;
            const dy = t.clientY - touchStartY;
            const dt = Date.now() - touchStartT;

            // Tap (no move + short)
            if (!touchHasMoved && dt < VideoStories.TIMINGS.tapMaxMs) {
              this._togglePlay();
              return;
            }

            // Swipe down (close)
            if (dy > VideoStories.TIMINGS.swipeDownThreshold && Math.abs(dy) > Math.abs(dx)) {
              this.close();
              return;
            }

            // Swipe horizontal
            if (Math.abs(dx) > VideoStories.TIMINGS.swipeHorizontalThreshold && Math.abs(dx) > Math.abs(dy)) {
              if (dx < 0) this._navigate(1);
              else this._navigate(-1);
            }
          });
        }
      }

      _togglePlay() {
        const video = this.modal.querySelector('.vs__modal-video');
        if (!video) return;
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      }

      _toggleMute() {
        const video = this.modal.querySelector('.vs__modal-video');
        if (!video) return;
        video.muted = !video.muted;
        this._userMuted = video.muted;
      }

      _syncPlayIcon() {
        const video = this.modal.querySelector('.vs__modal-video');
        const playI = this.modal.querySelector('.vs__icon-play');
        const pauseI = this.modal.querySelector('.vs__icon-pause');
        if (!video || !playI || !pauseI) return;
        playI.style.display = video.paused ? '' : 'none';
        pauseI.style.display = video.paused ? 'none' : '';
      }

      _syncMuteIcon() {
        const video = this.modal.querySelector('.vs__modal-video');
        const sI = this.modal.querySelector('.vs__icon-sound');
        const mI = this.modal.querySelector('.vs__icon-mute');
        if (!video || !sI || !mI) return;
        sI.style.display = video.muted ? 'none' : '';
        mI.style.display = video.muted ? '' : 'none';
      }

      _navigate(dir) {
        if (this.bubbles.length <= 1) return;
        const total = this.bubbles.length;
        const next = (this.activeIndex + dir + total) % total;
        this.activeIndex = next;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const frame = this.modal.querySelector('.vs__modal-frame');
        if (frame && !reduceMotion) {
          const px = VideoStories.TIMINGS.slideTranslatePx;
          frame.animate([
            { transform: `translateX(${dir > 0 ? px + 'px' : '-' + px + 'px'})`, opacity: 0.4 },
            { transform: 'translateX(0)', opacity: 1 }
          ], { duration: VideoStories.TIMINGS.slideAnimationMs, easing: 'ease-out' });
        }

        this._renderActive();

        const video = this.modal.querySelector('.vs__modal-video');
        if (video) {
          video.muted = this._userMuted ?? false;
          video.currentTime = 0;
          video.play().catch(() => { video.muted = true; video.play().catch(() => {}); });
        }

        this._preloadNext();
        this._pushDataLayer('video_start');
      }

      _preloadNext() {
        const next = (this.activeIndex + 1) % this.bubbles.length;
        const nextSrc = this.bubbles[next] && this.bubbles[next].dataset.videoSrc;
        if (!nextSrc) return;
        // hint preload via link[rel=prefetch] (best-effort, no decoding)
        let link = document.head.querySelector(`link[rel="prefetch"][href="${nextSrc}"]`);
        if (!link) {
          link = document.createElement('link');
          link.rel = 'prefetch';
          link.as = 'video';
          link.href = nextSrc;
          document.head.appendChild(link);
        }
      }
    });
  }
